<template>
  <login-wrapper v-if="branding" :branding="branding" :title="title" :loading="authState.loading">
    <template v-if="branding.isLoginAllowed">
      <login-form
        v-if="!branding.ssoSettings || !branding.ssoSettings.ssoLoginExclusive"
        class="mt-2"
        :branding="branding"
        :login-error="authState.loginError"
        @login="login"
        @reset="resetPassword"
      />
      <sso-login-button
        v-if="branding.ssoSettings"
        class="mt-2"
        :sso-settings="branding.ssoSettings"
      />
    </template>
    <template v-else>
      <div class="text-center mt-2">
        <sf-color-text color="blue-grey">
          {{ $t('registrationDisabledDescription') }}
        </sf-color-text>
      </div>
    </template>
  </login-wrapper>
</template>

<script setup lang="ts">
import SfColorText from '@/components/text/SfColorText.vue'
import { isQuotesPortal, useTitle } from '@/helpers'
import LoginForm from '@/modules/authentication/components/LoginForm.vue'
import LoginWrapper from '@/modules/authentication/components/LoginWrapper.vue'
import SsoLoginButton from '@/modules/authentication/components/SsoLoginButton.vue'
import { LoginError } from '@/modules/authentication/types'
import useAuthentication from '@/modules/authentication/useAuthentication'
import usePasswordReset from '@/modules/authentication/usePasswordReset'
import useSsoPopup from '@/modules/authentication/useSsoPopup'
import useBranding from '@/modules/portalSettings/useBranding'
import { useStateManager } from '@/store'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const { t } = useI18n()
useTitle(ref(t('windowTitle.Login')))
useStateManager().reset(false)
const currentRoute = useRoute()
const authError = currentRoute.query['auth_error']
if (authError === '0') {
  useAuthentication().setLoginError(LoginError.UNKNOWN_ERROR)
} else if (authError === '1') {
  useAuthentication().setLoginError(LoginError.USER_DEACTIVATED)
}

const authState = useAuthentication().state
const login = useAuthentication().login
const { branding } = useBranding()
const { openSsoPopup } = useSsoPopup()

const title = computed(() => {
  if (!branding.value?.isLoginAllowed) {
    return t('registrationDisabled')
  }
  return isQuotesPortal.value ? t('loginQuotesPortal') : t('login')
})

const resetPassword = (mail: string) => {
  usePasswordReset().resetPassword({
    mail: mail
  })
}

const handleSamlLoginSuccess = (event: MessageEvent) => {
  // Listen for SAML popup login success messages
  // Verify the message origin for security
  if (event.origin !== window.location.origin) return

  if (event.data.type === 'saml_login_success') {
    // The popup successfully completed SAML login
    // Tokens are already in localStorage, so we just need to reload
    window.location.reload()
  }
}

onMounted(() => {
  if (branding.value?.ssoSettings) {
    // Auto-start SSO if startsso query parameter is present
    if (currentRoute.query['startsso']) {
      openSsoPopup(branding.value.ssoSettings)
    }
    window.addEventListener('message', handleSamlLoginSuccess)
  }
})

onUnmounted(() => {
  window.removeEventListener('message', handleSamlLoginSuccess)
})
</script>
